import { sortBy, thru, times } from 'lodash'
import { useMemo } from 'react'

import { Item } from '../../models/Item'
import { Package } from '../../models/Package'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import Divider from '../app/design/Divider'
import Paragraph from '../app/design/Paragraph'
import { ClientSearchableSelect } from '../contacts/ClientSearchableSelect'
import { ContractSearchableSelect } from './ContractSearchableSelect'
import { TypeSearchableSelect } from './TypeSearchableSelect'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextArea } from '../common/fields/TextArea'
import { invoicePaymentOptions, sessionExpireOptions } from '../settings/Options'
import { Receipt, WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    packageObj: Package
    onUpdatePackage: (updatedPackage: Package) => void
    owner: User
    packageItems: Item[]
}

export const NewPackagePage = ({ packageObj, onUpdatePackage, owner, packageItems }: Props) => {
    const handleSelectClient = async (user: User | undefined) => {
        const client = await RecordFactory.getInstance().loadClient(user?.id ?? '')
        if (client !== undefined && (client.discount ?? 0) > 0) {
            const updatedPackageObj = packageObj.clone({
                client: user,
                discount_type: 'percent',
                discount: client.discount,
            })
            if (client.notify) {
                updatedPackageObj.channel = client.notify
            }
            onUpdatePackage(updatedPackageObj)
        }
        else {
            const updatedPackageObj = packageObj.clone({ client: user })
            if (client?.notify) {
                updatedPackageObj.channel = client.notify
            }
            onUpdatePackage(updatedPackageObj)
        }
    }

    const packagesSearchableItems = useMemo(() => {
        return sortBy(packageItems, (packageItem) => packageItem.name?.toLowerCase() ?? '').map(
            (packageItem) => ({
                id: packageItem.id ?? '',
                title: packageItem.name ?? '',
                subtitle:
                    packageItem.rate === undefined ? (
                        'TBD'
                    ) : (
                        <>
                            <span
                                className={
                                    packageItem.discountAmount !== undefined ? 'line-through' : ''
                                }
                            >
                                {currencyFormat(packageItem.rate)}
                            </span>{' '}
                            {packageItem.rate !== undefined &&
                            packageItem.discountAmount !== undefined
                                ? currencyFormat(packageItem.rate - packageItem.discountAmount)
                                : ''}
                        </>
                    ),
                tertiaryView:
                    packageItem.iconConfig === undefined ? undefined : packageItem.iconConfig
                          .type === 'image' ? (
                        <img src={packageItem.iconConfig.uri} className="size-9 rounded-full" />
                    ) : (
                        <Icon
                            icon={packageItem.iconConfig.icon}
                            background="round"
                            className="size-9"
                        />
                    ),
                packageItem,
            })
        )
    }, [packageItems])
    const selectedPackage = packageObj.item
    const selectedPackageItem =
        selectedPackage !== undefined
            ? thru(
                  packagesSearchableItems.find(
                      (packageItem) => packageItem.id === selectedPackage.id
                  ) ?? null,
                  (packageItem) => {
                      if (packageItem === null) {
                          return null
                      }

                      const iconConfig = packageItem.packageItem.iconConfig

                      const newPackageItem = {
                          ...packageItem,
                          tertiaryView:
                              iconConfig === undefined ? undefined : iconConfig.type === 'image' ? (
                                  <img src={iconConfig.uri} className="size-6 rounded-full" />
                              ) : (
                                  <Icon
                                      icon={iconConfig.icon}
                                      background="round"
                                      className="size-6"
                                  />
                              ),
                      }
                      delete (newPackageItem as any).subtitle
                      return newPackageItem
                  }
              )
            : null
    const handleSelectNewPackageItem = (newPackageItem: Item | null) => {
        if (newPackageItem === null) {
            onUpdatePackage(packageObj.clone({ item: undefined }))
            return
        }

        onUpdatePackage(
            packageObj.clone({
                item: newPackageItem,
                memo: newPackageItem.memo,
                total: newPackageItem.deposit ?? 0,
                expires: newPackageItem.expires,
                capacity_type: newPackageItem.capacityType,
                capacity: newPackageItem.capacity,
                ...(newPackageItem.allowSalesTax && owner.allowSalesTax && owner.taxcode
                    ? {
                          nexus: 'pro',
                          tax: owner.taxcode.rate,
                          taxcode: owner.taxcode,
                      }
                    : {
                          nexus: undefined,
                          tax: undefined,
                          taxcode: undefined,
                      }),
                ...(owner.allowDiscount &&
                newPackageItem.discount &&
                newPackageItem.discountType === 'percent'
                    ? {
                          discount: newPackageItem.discount,
                          discount_type: newPackageItem.discountType,
                      }
                    : {}),
                ...(owner.allowRecord && newPackageItem.form ? { form: newPackageItem.form } : {}),
                ...(owner.allowContract && newPackageItem.contract
                    ? { contract: newPackageItem.contract }
                    : {}),
            })
        )
    }

    const receipt = useMemo((): Receipt => {
        return {
            modifiers: {
                subtotal: {
                    amount: packageObj.total ?? 0,
                    onUpdate: (newAmount) =>
                        onUpdatePackage(packageObj.clone({ total: newAmount })),
                },
                giftCertificate: owner.allowGift
                    ? {
                          selectedGift: packageObj.gift,
                          onSelectGift: (gift) => {
                              if (gift !== undefined) {
                                  onUpdatePackage(
                                      packageObj.clone({
                                          gift,
                                          discount_type: 'rate',
                                          discount: gift.balance ?? 0,
                                      })
                                  )
                              }
                            else {
                                  onUpdatePackage(packageObj.clone({ gift: undefined }))
                              }
                          },
                      }
                    : undefined,
                discount: owner.allowDiscount ? {} : undefined,
                salesTax: owner.allowSalesTax
                    ? {
                          amount: packageObj.taxAmount ?? null,
                          parenthetical:
                              packageObj.taxcode !== undefined
                                  ? `${packageObj.taxcode.rate ?? 0}%`
                                  : null,
                      }
                    : undefined,
                surcharge: owner.allowSurcharge
                    ? {
                          amount: packageObj.allowSurcharge ? packageObj.surchargeAmount : null,
                          onAdd: () => onUpdatePackage(packageObj.clone({ allow_surcharge: true })),
                          onRemove: () =>
                              onUpdatePackage(packageObj.clone({ allow_surcharge: false })),
                      }
                    : undefined,
            },
            total: packageObj.chargeAmount,
        }
    }, [packageObj])

    const handlePaymentPlanChange = (item: { id: string } | null) => {
        if (item === null) {
            return
        }
        const newPackageObj = packageObj.clone()
        newPackageObj.allowAnyPayment = item.id
        onUpdatePackage(newPackageObj)
    }

    return (
        <div className="mt-12 flex flex-col items-center justify-start gap-y-6">
            <WorkflowPage.Card title="General">
                <WorkflowPage.IconSection title="Client (required)" icon="user.large">
                    <ClientSearchableSelect
                        mode="server"
                        selectedClient={packageObj.client}
                        selectedSubaccount={packageObj.subaccount}
                        onSelectClient={handleSelectClient}
                        onSelectSubaccount={(subaccount) =>
                            onUpdatePackage(
                                packageObj.clone({ client: subaccount?.client, subaccount })
                            )
                        }
                        shouldPreserveLastSelection={true}
                        canAddNew
                        shouldIncludeSubaccounts
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection title="Package (required)" icon="package2.large">
                    <SearchableSelect
                        items={packagesSearchableItems}
                        onSelectItem={(item) =>
                            handleSelectNewPackageItem(item?.packageItem ?? null)
                        }
                        selectedItemOverride={selectedPackageItem}
                        titleWeight="bold"
                        placeholder="Type to search"
                        shouldPreserveLastSelection
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection title="Sessions" icon="hash.large">
                    <SearchableSelect
                        items={[
                            ...times(100, (index) => ({
                                id: `${index + 1}`,
                                title: `${index + 1}`,
                            })),
                            { id: '144', title: '144' },
                            { id: '192', title: '192' },
                            { id: '240', title: '240' },
                        ]}
                        onSelectItem={(item) =>
                            onUpdatePackage(packageObj.clone({ capacity: item?.id }))
                        }
                        selectedItemID={packageObj.capacity?.toString()}
                        size="medium"
                        shouldPreserveLastSelection
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection title="Expiration after" icon="alarmClock.large">
                    <SearchableSelect
                        items={sessionExpireOptions.map((option) => ({
                            id: option.value,
                            title: option.label,
                        }))}
                        onSelectItem={(item) =>
                            onUpdatePackage(packageObj.clone({ expires: item?.id }))
                        }
                        selectedItemID={packageObj.expires?.toString()}
                        size="medium"
                        shouldPreserveLastSelection
                    />
                </WorkflowPage.IconSection>
            </WorkflowPage.Card>

            <WorkflowPage.Card title="Total">
                <div>
                    <WorkflowPage.ReceiptView
                        receipt={receipt}
                        transaction={packageObj}
                        onUpdateTransaction={onUpdatePackage}
                        owner={owner}
                        client={packageObj.client}
                    />

                    <Divider />

                    <div className="flex items-stretch justify-center gap-x-4">
                        <div className="flex shrink-0 items-start justify-center">
                            <Icon icon="cards.large" className="text-gray-500" />
                        </div>
                        <div className="grow">
                            <span>Payment plan</span>
                            <Paragraph className="text-paragraph-sm text-gray-600">
                                Specify the max number of payments for this invoice and the minimum
                                payment amount. Note that your clients can opt to pay the full
                                invoice balance at any time.
                            </Paragraph>
                        </div>
                        <div className="flex shrink-0 items-center justify-center">
                            <SearchableSelect
                                items={invoicePaymentOptions.map((option) => ({
                                    id: option.value,
                                    title: option.label,
                                }))}
                                selectedItemID={packageObj.allowAnyPayment ?? ''}
                                onSelectItem={handlePaymentPlanChange}
                                size="medium"
                                shouldPreserveLastSelection
                            />
                        </div>
                    </div>
                </div>
            </WorkflowPage.Card>

            <WorkflowPage.NotificationsSection
                transaction={packageObj}
                onUpdateTransaction={onUpdatePackage}
                sendViaDescription="Choose how you'd like your client to be sent this appointment"
                reminderDescription="If a package remains unpaid, choose how often you’d like your client to be politely reminded until payment is received."
            />

            <WorkflowPage.Card title="More options" defaultIsCollapsed>
                <WorkflowPage.IconSection
                    title="Memo"
                    icon="note.large"
                    secondaryText={`${(packageObj.memo ?? '').length} / 1500`}
                >
                    <TextArea
                        size="medium"
                        isAutoResizing
                        placeholder="Add a memo"
                        className="overlow-y-hidden"
                        value={packageObj.memo ?? ''}
                        onChange={(event) =>
                            onUpdatePackage(packageObj.clone({ memo: event.target.value }))
                        }
                        maxLength={1500}
                        onClear={() => onUpdatePackage(packageObj.clone({ memo: '' }))}
                    />
                </WorkflowPage.IconSection>

                {owner.allowRecord && (
                    <WorkflowPage.IconSection title="Attach a form" icon="field.large">
                        <TypeSearchableSelect
                            owner={owner}
                            selectedForm={packageObj.form}
                            onSelectForm={(form) => onUpdatePackage(packageObj.clone({ form }))}
                        />
                    </WorkflowPage.IconSection>
                )}

                {owner.allowRecord && (
                    <WorkflowPage.IconSection title="Attach a contract" icon="pen.large">
                        <ContractSearchableSelect
                            selectedContract={packageObj.contract}
                            onSelectContract={(contract) =>
                                onUpdatePackage(packageObj.clone({ contract }))
                            }
                        />
                    </WorkflowPage.IconSection>
                )}
            </WorkflowPage.Card>
        </div>
    )
}
